/**
 * 組織の形の判定（象限分布による統一判定）。
 *
 * 【なぜ作り直したか（2026-08-22）】
 *  それまで「1-2 組織タイプの型」は個人の優勢軸の組み合わせ（タイプコード）の
 *  最多占有率で、「1-3 社員の分布」は散布図の象限比率で判定していた。
 *  土台が2つあるため、同じレポートに
 *    「似た持ち味の方が集まっています（集中型）」
 *    「持ち味が四方に散らばっている（分布）」
 *  のように矛盾した結論が並ぶことが繰り返し起きた。
 *  D11・C10・B7・A4 と明らかにCD寄りの組織が集中型と出るのも同じ原因。
 *  形の判定を象限分布に一本化し、型と分布を同じ結果から作る。
 *
 * 【散布図の座標について】
 *  x=(C+D)−(A+B) / y=(A+D)−(B+C)。組織平均は打ち消し合って中心に寄るが、
 *  個人の点は打ち消されないため、象限分布は二極を正しく捉える。
 *
 * 【仕様書§04からの変更】
 *  仕様書は型を「分極率 → 最多タイプ占有率」で判定するとしているが、上記の理由で
 *  象限分布に置き換えた。閾値は DB04 の 04-基準（象限比率・中心付近比率）を使う。
 *  廣瀬様への確認事項。
 */
import type { AxisKey } from './gb74Report';
import { centerThreshold, type QuadrantCounts } from './orgDistributionV3';

/** 中心付近とみなす範囲（|x| と |y| がこれ以下）。DB04は半径3。 */
export const CENTER_RADIUS = 3;

/** 形を判定する最小人数（DB14 14-型-判定不可）。 */
export const SHAPE_MIN_MEMBERS = 15;

/** 4象限が「均等」とみなせる最大の開き（最大比率 − 最小比率）。 */
export const EVEN_MAX_GAP = 0.15;

/**
 * 「寄っている人」の中での比率に対する偶然の水準（自前のシミュレーション・各4万試行）。
 * 無作為に答えた社員をn人集めたときの分位点。
 *
 * 【なぜ独自に測ったか】
 *  DB04 の基準は「全回答者に対する単独象限の比率」で、隣接ペアや対角の基準を持たない。
 *  隣接ペアは補い合う関係（AB＋CD＝100%）なので、単独象限の基準をそのまま当てると
 *  必ずどちらかが超えてしまい、ほぼ全ての組織が同系統型になってしまった。
 *  ペアにはペアの偶然の水準が要る（22名で中央値65%・75%点71%・95%点81%）。
 *
 * 【2段階にしている理由】
 *  95%点だけで判定すると、見た目に明らかな寄り（左右で12名対4名など）でも
 *  「中間型」に落ちてしまい、読み手の感覚と合わない。
 *  形の名前は 75%点（偶然の上位4分の1に入る寄り方）で付け、
 *  95%点に届かないものは「偶然の範囲と明確には区別できない」と注記する。
 *
 * [回答者数, 隣接75%, 隣接95%, 単独75%, 単独95%, 対角の弱い側75%, 同95%]
 */
const PLACED_NULL: ReadonlyArray<
  readonly [number, number, number, number, number, number, number]
> = [
  [15, 0.75, 0.88, 0.5, 0.6, 0.27, 0.33],
  [22, 0.71, 0.81, 0.44, 0.54, 0.27, 0.32],
  [30, 0.68, 0.77, 0.42, 0.5, 0.26, 0.32],
  [50, 0.66, 0.73, 0.39, 0.46, 0.26, 0.29],
  [100, 0.63, 0.68, 0.36, 0.41, 0.25, 0.28],
];

export interface PlacedNullLevels {
  /** 形の名前を付ける基準（75%点） */
  adjacent: number;
  single: number;
  diagWeak: number;
  /** 断定してよい基準（95%点）。届かなければ参考値として注記する */
  adjacent95: number;
  single95: number;
  diagWeak95: number;
}

/** 人数から偶然の水準を引く（表に無い人数は線形補間）。 */
export function placedNullLevels(n: number): PlacedNullLevels {
  const t = PLACED_NULL;
  const pick = (r: (typeof t)[number]): PlacedNullLevels => ({
    adjacent: r[1],
    adjacent95: r[2],
    single: r[3],
    single95: r[4],
    diagWeak: r[5],
    diagWeak95: r[6],
  });
  if (n <= t[0][0]) return pick(t[0]);
  const last = t[t.length - 1];
  if (n >= last[0]) return pick(last);
  for (let i = 1; i < t.length; i++) {
    if (n <= t[i][0]) {
      const lo = t[i - 1];
      const hi = t[i];
      const w = (n - lo[0]) / (hi[0] - lo[0]);
      const mix = (a: number, b: number) => a + (b - a) * w;
      return {
        adjacent: mix(lo[1], hi[1]),
        adjacent95: mix(lo[2], hi[2]),
        single: mix(lo[3], hi[3]),
        single95: mix(lo[4], hi[4]),
        diagWeak: mix(lo[5], hi[5]),
        diagWeak95: mix(lo[6], hi[6]),
      };
    }
  }
  return pick(last);
}

export type OrgShapeV3 = '中心集中型' | '二極型' | '同系統型' | '集中型' | '分散型' | '中間型';

/** 隣り合う象限の組（ダイヤモンド上で辺を共有する4組）。 */
export const ADJACENT_PAIRS: Array<[AxisKey, AxisKey]> = [
  ['C', 'D'], // 右（人と変化）
  ['A', 'B'], // 左（筋道と守り）
  ['A', 'D'], // 上（考えることと動くこと）
  ['B', 'C'], // 下（守りと支え合い）
];

/** 対角の組。 */
export const DIAGONAL_PAIRS: Array<[AxisKey, AxisKey]> = [
  ['A', 'C'],
  ['B', 'D'],
];

const AXES: AxisKey[] = ['A', 'B', 'C', 'D'];

export interface OrgShapeResultV3 {
  shape: OrgShapeV3 | null;
  /** 二極型のときの対角、同系統型のときの隣接ペア、集中型のときの象限 */
  diagonal: [AxisKey, AxisKey] | null;
  adjacent: [AxisKey, AxisKey] | null;
  quadrant: AxisKey | null;
  /** 3軸が優勢のときの「薄い軸」。象限の集中として語らないための例外 */
  thinAxis: AxisKey | null;
  /** 3象限にだけ人がいるときの「空いている象限」 */
  emptyQuadrant: AxisKey | null;
  counts: QuadrantCounts;
  memberCount: number;
  /** 参考値。象限の比率は「寄っている人」の中での割合 */
  rates: Record<AxisKey, number>;
  centerRate: number;
  /** 偶然の範囲と明確には区別できないか（95%点に届いていない） */
  isChanceLevel: boolean;
}

/**
 * 象限分布からタイプコードを組み立てる。
 *
 * 【なぜ象限から出すか（2026-08-22）】
 *  以前は優勢率（その軸が7点以上の社員の割合）から出していたが、型は象限分布で
 *  判定しているため「コードC なのに二極型」のような食い違いが起きていた。
 *  コードと型を同じ土台から出すことで、必ず対応するようにする。
 *  仕様書§04 手順2からの逸脱（廣瀬様への確認事項）。
 *
 * 型ごとに、その型を決めた象限をそのままコードにする。
 * 方向が定まらない型（中心集中・分散）は「全方」。
 */
export function orgCodeFromShape(r: OrgShapeResultV3): string {
  const byRate = (a: AxisKey, b: AxisKey) =>
    r.rates[b] - r.rates[a] || AXES.indexOf(a) - AXES.indexOf(b);

  if (r.shape === '中心集中型' || r.shape === '分散型') return '全方';
  if (r.shape === '集中型' && r.quadrant) return r.quadrant;
  if (r.shape === '二極型' && r.diagonal) return [...r.diagonal].sort(byRate).join('');
  if (r.shape === '同系統型' && r.adjacent) return [...r.adjacent].sort(byRate).join('');

  // 中間型・判定なし。平均(25%)を上回る象限を多い順に並べる。
  const picked = AXES.filter((k) => r.rates[k] > 0.25).sort(byRate);
  return picked.length > 0 ? picked.join('') : '全方';
}

/** 4軸から散布図の座標を出す（仕様書§05）。 */
export function scatterXY(axis: Record<AxisKey, number>): { x: number; y: number } {
  return {
    x: axis.C + axis.D - (axis.A + axis.B),
    y: axis.A + axis.D - (axis.B + axis.C),
  };
}

/** 象限ごとの人数を数える。中心付近は象限に入れない。 */
export function quadrantCountsOf(members: Array<Record<AxisKey, number>>): QuadrantCounts {
  const counts: QuadrantCounts = { A: 0, B: 0, C: 0, D: 0, center: 0 };
  members.forEach((m) => {
    const { x, y } = scatterXY(m);
    if (Math.abs(x) <= CENTER_RADIUS && Math.abs(y) <= CENTER_RADIUS) {
      counts.center += 1;
      return;
    }
    // 左上=A / 右上=D / 右下=C / 左下=B
    if (x < 0) counts[y >= 0 ? 'A' : 'B'] += 1;
    else counts[y >= 0 ? 'D' : 'C'] += 1;
  });
  return counts;
}

/**
 * 形を判定する。判定順は次のとおり。
 *   ① 人数不足        → 判定しない
 *   ② 3軸が優勢       → 象限の集中として語らない（薄い軸で表現する）
 *   ③ 中心付近が基準以上 → 中心集中型
 *   ④ 対向する2象限   → 二極型
 *   ⑤ 単独の象限が基準以上 → 集中型
 *   ⑥ 隣り合う2象限   → 同系統型
 *   ⑦ 1象限が空       → 中間型（3方向に広がっている、として文言を分ける）
 *   ⑧ 4象限が均等     → 分散型
 *   ⑨ それ以外        → 中間型
 *
 * ⑤を⑥より先に見るのは、1つの象限だけで基準を超えている場合、それを含む
 * 隣接ペアも自動的に超えてしまうため。強いほうを先に採る。
 */
export function classifyOrgShapeV3(
  members: Array<Record<AxisKey, number>>,
  /**
   * 優勢率が3軸ぶん高いときの「薄い軸」。象限の集中として語らないための例外。
   * 優勢率の判定は orgTypeV3 側で行い、その結果だけを受け取る。
   */
  thinAxisIfThreeDominant?: AxisKey | null
): OrgShapeResultV3 {
  const n = members.length;
  const counts = quadrantCountsOf(members);
  const centerRate = counts.center / Math.max(1, n);

  /**
   * 象限どうしの比較は「どこかに寄っている人」の中で行う。
   *
   * 中央付近の人まで分母に入れると、中央が多い組織ではどの象限も基準に届かず、
   * 実際にはCD寄りでも中間型になってしまう。中央の人は方向を持たないので、
   * 「寄っている人のうち、どちらへ寄っているか」を見るのが実態に合う。
   * 中央そのものの多さは、この前段の 中心集中型 の判定で見ている。
   */
  const placed = Math.max(1, n - counts.center);
  const rate = (k: AxisKey) => counts[k] / placed;
  const rates = { A: rate('A'), B: rate('B'), C: rate('C'), D: rate('D') };
  const lv = placedNullLevels(n);

  const base: OrgShapeResultV3 = {
    shape: null,
    diagonal: null,
    adjacent: null,
    quadrant: null,
    thinAxis: null,
    emptyQuadrant: null,
    counts,
    memberCount: n,
    rates,
    centerRate,
    isChanceLevel: false,
  };

  if (n < SHAPE_MIN_MEMBERS) return base;

  // ② 3軸優勢。散布図の座標は相対位置なので、1軸だけ低い組織は必ずその反対側の
  //    象限に全員が集まる。象限の集中として語ると実態と食い違う。
  if (thinAxisIfThreeDominant) {
    return { ...base, shape: '中間型', thinAxis: thinAxisIfThreeDominant };
  }

  // ③ 中心集中
  if (centerRate >= centerThreshold(n)) return { ...base, shape: '中心集中型' };

  // ④ 二極（対向する2象限の両方に、偶然では説明できないほど人がいる）
  for (const [x, y] of DIAGONAL_PAIRS) {
    const others = AXES.filter((k) => k !== x && k !== y);
    const weakEnd = Math.min(rates[x], rates[y]);
    const strongOther = Math.max(...others.map((k) => rates[k]));
    if (weakEnd >= lv.diagWeak && weakEnd > strongOther) {
      return {
        ...base,
        shape: '二極型',
        diagonal: [x, y],
        isChanceLevel: weakEnd < lv.diagWeak95,
      };
    }
  }

  // ⑤⑥ 単独の象限への集中と、隣り合う2象限への寄り。
  //
  // どちらを採るかは「それぞれの偶然の水準をどれだけ上回っているか」で決める。
  // 単独を先に見ると、A45%・B45% のように2つが拮抗している組織でも
  // 単独の基準(44%)をわずかに超えるため「集中型(A)」になってしまう。
  // 実態は AB への寄りなので、超え方の大きいほうを採る。
  const top = AXES.reduce((a, b) => (rates[a] >= rates[b] ? a : b));
  const singleScore = rates[top] >= lv.single ? rates[top] / lv.single : 0;

  let best: { pair: [AxisKey, AxisKey]; sum: number } | null = null;
  for (const pair of ADJACENT_PAIRS) {
    const [x, y] = pair;
    const others = AXES.filter((k) => k !== x && k !== y);
    const sum = rates[x] + rates[y];
    const weakEnd = Math.min(rates[x], rates[y]);
    const strongOther = Math.max(...others.map((k) => rates[k]));
    if (sum >= lv.adjacent && weakEnd >= strongOther && (!best || sum > best.sum)) {
      best = { pair, sum };
    }
  }
  const adjacentScore = best ? best.sum / lv.adjacent : 0;

  if (singleScore > 0 && singleScore >= adjacentScore) {
    return {
      ...base,
      shape: '集中型',
      quadrant: top,
      isChanceLevel: rates[top] < lv.single95,
    };
  }
  if (best) {
    return {
      ...base,
      shape: '同系統型',
      adjacent: best.pair,
      isChanceLevel: best.sum < lv.adjacent95,
    };
  }

  // ⑦ 3象限にだけ人がいる（1つの向きがまるごと空いている）
  const empty = AXES.filter((k) => counts[k] === 0);
  if (empty.length === 1) {
    return { ...base, shape: '中間型', emptyQuadrant: empty[0] };
  }

  // ⑧ 分散（4象限すべてに人がいて、最大と最小の開きが小さい）
  const vals = AXES.map((k) => rates[k]);
  if (Math.min(...vals) > 0 && Math.max(...vals) - Math.min(...vals) <= EVEN_MAX_GAP) {
    return { ...base, shape: '分散型' };
  }

  // ⑧ それ以外
  return { ...base, shape: '中間型' };
}
